import { Calendar, Select } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useMemo, useState } from 'react';

import {
  forceDateIntoRange,
  getSelectableYears,
  getValidDateRange,
  isWithinRange,
  now,
} from '../../common/utils/dateUtils';
import { FAIcon, FATypes } from '../adapters/fontAwesomeAdapters';
import SegmentedControl from '../nav/SegmentedControl';
import { Popup } from '../nav/navElements';
import { useDateFormats } from '../../utils/dateFormatting';
import { useDateRangePickerStyles } from './DateRangePicker.styles';

const { Option } = Select;

// SegmentedControl only supports 2-5 options; cap the year selector so a large
// validRangeYearsBack can't crash the calendar header.
const MAX_YEAR_SEGMENTS = 5;

function CustomCalendar({ value, onChange, validRangeYearsBack }) {
  const intl = useIntl();
  const { styles } = useDateRangePickerStyles();

  const validRange = useMemo(
    () => getValidDateRange(validRangeYearsBack),
    [validRangeYearsBack]
  );

  const yearOptions = useMemo(
    () =>
      getSelectableYears(validRangeYearsBack)
        .slice(-MAX_YEAR_SEGMENTS)
        .map(year => ({ label: year, value: year })),
    [validRangeYearsBack]
  );

  const monthLabels = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        intl.formatDate(new Date(2000, i, 1), { month: 'long' })
      ),
    [intl]
  );

  const calendarValue = value ?? now();

  const clamp = date =>
    forceDateIntoRange(date, { min: validRange[0], max: validRange[1] });

  return (
    <Calendar
      className={styles.calendar}
      fullscreen={false}
      value={calendarValue}
      onChange={val => onChange?.(val)}
      validRange={validRange}
      headerRender={({ value: val, onChange: onHeaderChange }) => (
        <div>
          <SegmentedControl
            value={val?.year()}
            onChange={year =>
              onHeaderChange(clamp((val?.clone() ?? now()).year(year)))
            }
            options={yearOptions}
          />
          <div
            className={classNames(
              styles.calendarMonthSelector,
              'DateRangePicker__Calendar__MonthSelector'
            )}
          >
            <Select
              value={val?.month()}
              onChange={month =>
                onHeaderChange(clamp((val?.clone() ?? now()).month(month)))
              }
              variant="borderless"
              size="small"
              getPopupContainer={trigger => trigger.parentNode}
            >
              {monthLabels.map((label, i) => {
                const candidate = (val?.clone() ?? now()).month(i);
                const inRange = isWithinRange(
                  candidate,
                  { min: validRange[0], max: validRange[1] },
                  'month'
                );
                return (
                  <Option key={label} value={i} disabled={!inRange}>
                    {label}
                  </Option>
                );
              })}
            </Select>
          </div>
        </div>
      )}
      cellRender={date => date.date()}
    />
  );
}

export function SingleDateField({ value, onChange, validRangeYearsBack = 2 }) {
  const { styles } = useDateRangePickerStyles();
  const { numeric } = useDateFormats();
  const [inputText, setInputText] = useState('');
  const [editing, setEditing] = useState(false);

  const validRange = useMemo(
    () => getValidDateRange(validRangeYearsBack),
    [validRangeYearsBack]
  );

  const displayText = value ? value.format(numeric) : '';

  const tryParse = text => {
    const parsed = moment(text, numeric, true);
    return parsed.isValid() &&
      isWithinRange(parsed, { min: validRange[0], max: validRange[1] })
      ? parsed
      : null;
  };

  // Only buffer the text while typing; commit on blur/Enter so a single
  // refetch happens when the user finishes, not on every keystroke.
  const handleChange = e => {
    setInputText(e.target.value);
    if (!editing) setEditing(true);
  };

  const commitInput = text => {
    setEditing(false);
    const parsed = tryParse(text);
    if (parsed) {
      onChange?.(parsed);
    } else if (text.trim()) {
      // Unparseable/out-of-range text: keep the existing value rather than
      // wiping it. The input falls back to displayText once editing ends.
      onChange?.(value ?? null);
    }
  };

  return (
    <div
      className={classNames(
        styles.calendarPopup,
        'DateRangePicker__CalendarPopup'
      )}
    >
      <Popup
        label={
          <div
            className={classNames(
              styles.inputLike,
              'DateRangePicker__InputLike width-100p',
              { 'has-value': value }
            )}
          >
            <FAIcon
              icon="calendar"
              className="icon-24"
              type={FATypes.REGULAR}
            />
            <input
              className={classNames(
                styles.dateInput,
                'DateRangePicker__DateInput'
              )}
              value={editing ? inputText : displayText}
              placeholder={numeric}
              onChange={handleChange}
              onFocus={e => {
                setEditing(true);
                setInputText(displayText);
                e.stopPropagation();
              }}
              onBlur={() => commitInput(inputText)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  commitInput(inputText);
                  e.target.blur();
                }
              }}
            />
          </div>
        }
        noIcon
        getPopupContainer={cnt => cnt.nextSibling}
        trigger="click"
      >
        <CustomCalendar
          value={value}
          onChange={onChange}
          validRangeYearsBack={validRangeYearsBack}
        />
      </Popup>
      <div className={styles.calendarContainer} />
    </div>
  );
}
